import React from 'react';
import { useIntl } from 'react-intl';
import type { TeamInvitePreview } from '../../../../music24-api/src/app/components/Team/teamInvitePreview';
import { TypedMemo } from '../common/types';
import type { WorkspaceSummary } from '../workspaces/useWorkspaces';
import type { WorkspaceSwitchFailure } from '../workspaces/useSwitchWorkspace';
import { TeamAcceptWorkspaceRow } from './TeamAcceptWorkspaceRow';
import { TeamAcceptHeading } from './TeamAcceptHeading';
import { TeamAcceptAlreadyMemberHeading } from './TeamAcceptAlreadyMemberHeading';
import { TeamAcceptJoinBenefits } from './TeamAcceptJoinBenefits';
import { TeamAcceptSignedInAs } from './TeamAcceptSignedInAs';
import { TeamAcceptInviteAlert } from './TeamAcceptInviteAlert';
import { TeamAcceptInviteActions } from './TeamAcceptInviteActions';
import type { TeamAcceptInviteState } from './teamAcceptInviteState';

interface TeamAcceptInviteStepProps {
  preview: TeamInvitePreview;
  state: TeamAcceptInviteState;
  /** The caller's own workspace, for the c1 benefit line. Null before `workspaces.list` loads. */
  homeWorkspace: WorkspaceSummary | null;
  /** The caller's row of the invite workspace (c5), when the list holds it. */
  memberWorkspace: WorkspaceSummary | null;
  /** The last failed join, if any. */
  acceptError: unknown;
  /** c5: the last failed switch into the invite workspace, if any. */
  switchFailure: WorkspaceSwitchFailure | null;
  isJoining: boolean;
  /** c5: the switch into the invite workspace runs. */
  isOpening: boolean;
  onDecline: () => void;
  onJoin: () => void;
  onOpenWorkspace: () => void;
}

/**
 * design: workspaces c1, c4, c5, c6, c7. "Join {workspace}", who invited you, the workspace row, what
 * you get, then "Not now" and one primary action. There is no step that leaves the own workspace.
 */
const TeamAcceptInviteStepComponent: React.FC<TeamAcceptInviteStepProps> = ({
  preview,
  state,
  homeWorkspace,
  memberWorkspace,
  acceptError,
  switchFailure,
  isJoining,
  isOpening,
  onDecline,
  onJoin,
  onOpenWorkspace,
}) => {
  const intl = useIntl();
  const isAlreadyMember = state === 'alreadyMember';
  const roleLabel =
    memberWorkspace?.role === 'OWNER'
      ? intl.formatMessage({ id: 'app.sml.team.role.owner', defaultMessage: 'Owner' })
      : intl.formatMessage({ id: 'app.sml.team.role.member', defaultMessage: 'Member' });

  return (
    <>
      {isAlreadyMember ? (
        <TeamAcceptAlreadyMemberHeading workspaceName={preview.workspaceName} />
      ) : (
        <TeamAcceptHeading
          isLeadMasked
          title={intl.formatMessage(
            { id: 'app.sml.team.accept.joinTitle', defaultMessage: 'Join {workspace}' },
            { workspace: preview.workspaceName }
          )}
          lead={intl.formatMessage(
            {
              id: 'app.sml.team.accept.invitedToWork',
              defaultMessage: '{inviter} invited you to work on their artists, pages and campaigns.',
            },
            { inviter: preview.inviterEmail }
          )}
        />
      )}
      <div className="mb-3">
        <TeamAcceptWorkspaceRow
          workspaceName={preview.workspaceName}
          plan={preview.plan}
          artistsCount={preview.artistsCount}
          seatsUsed={preview.seatsUsed}
          seatsLimit={preview.seatsLimit}
          seatsFull={state === 'full'}
          roleLabel={isAlreadyMember ? roleLabel : undefined}
        />
      </div>
      {state === 'ready' && (
        <TeamAcceptJoinBenefits workspaceName={preview.workspaceName} homeWorkspace={homeWorkspace} />
      )}
      <TeamAcceptInviteAlert
        state={state}
        workspaceName={preview.workspaceName}
        acceptError={acceptError}
        switchFailure={switchFailure}
      />
      {(state === 'ready' || state === 'full') && <TeamAcceptSignedInAs className="mb-3" />}
      <TeamAcceptInviteActions
        state={state}
        workspaceName={preview.workspaceName}
        isWorkspaceActive={memberWorkspace?.isActive === true}
        isJoining={isJoining}
        isOpening={isOpening}
        onDecline={onDecline}
        onJoin={onJoin}
        onOpenWorkspace={onOpenWorkspace}
      />
    </>
  );
};

export const TeamAcceptInviteStep = TypedMemo(TeamAcceptInviteStepComponent);
